import { BOARD_COLUMNS } from './constants';
import { Skeleton, SkeletonGroup, TaskCardSkeleton } from '@/components/Skeleton';

/**
 * The board's own shape, greyed out.
 *
 * Column headers keep their real icons and labels — those are static, we know them
 * before the request returns, and showing them means the header row does not move
 * when the data lands. Only the counts and the cards are unknown.
 *
 * The per-column card counts are fixed, not random: a skeleton that reshuffles on
 * every render reads as content arriving and then changing its mind.
 */
const CARDS_PER_COLUMN = [3, 2, 1, 2, 1];

export function BoardSkeleton({ desktop }: { desktop: boolean }) {
  return (
    <div className="flex h-full flex-col">
      <header
        className="flex shrink-0 items-center gap-3 border-b px-4 py-3"
        style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
      >
        <h1 className="text-base font-semibold tracking-tight">Board</h1>
        <Skeleton width={150} height={11} />
      </header>

      <SkeletonGroup
        label="Loading your board"
        className={
          desktop
            ? 'flex h-full gap-3 overflow-hidden p-4'
            : 'flex min-h-0 flex-1 flex-col gap-2 p-3'
        }
      >
        {desktop ? (
          BOARD_COLUMNS.map((column, index) => (
            <section
              key={column.status}
              className="flex min-h-0 w-[300px] shrink-0 flex-col rounded-xl border"
              style={{ borderColor: 'var(--border)', background: 'var(--surface-sunken)' }}
            >
              <header className="flex shrink-0 items-center gap-2 px-3 py-2.5">
                <column.icon size={15} style={{ color: column.colorVar }} />
                <h2 className="text-[13px] font-semibold">{column.label}</h2>
                <Skeleton width={20} height={16} radius={5} />
              </header>

              <div className="flex flex-col gap-2 px-2 pb-2">
                {Array.from({ length: CARDS_PER_COLUMN[index] ?? 1 }, (_, i) => (
                  <TaskCardSkeleton key={i} />
                ))}
              </div>
            </section>
          ))
        ) : (
          // One pane's worth: the phone only ever shows a single column at a time.
          <>
            <TaskCardSkeleton />
            <TaskCardSkeleton />
            <TaskCardSkeleton lines={1} />
          </>
        )}
      </SkeletonGroup>
    </div>
  );
}
